import { Integration, Integrators, IntegratorsLabel, Shop } from "@/types/v2";
import { useMemo, useState } from "react";
import { shop } from "@/repositories/shop";
import { showErrorToast, showSuccessToast } from "@/lib/toast";
import { AddCustomLabelDialog } from "@/components/CustomLabels/AddCustomLabelDialog";
import { CustomLabelList } from "@/components/CustomLabels/CustomLabelList";
import { Button } from "@/components/Base/Button";
import { Icon } from "@/components/Common/Icon";
import { httpsCallables } from "@/repositories/httpsCallables";
import { captureException } from "@/services/sentry";

type CustomLabelSettingsProps = {
  shopData: Shop;
  syncWithSource: () => void;
  integration?: Integration;
};

export const CustomLabelSettings = ({
  shopData,
  syncWithSource,
  integration,
}: CustomLabelSettingsProps) => {
  const shopId = useMemo(() => shopData.id, [shopData]);
  const customLabels = useMemo(() => shopData.customLabels ?? [], [shopData]);
  const [syncing, setSyncing] = useState(false);

  const handleSaveLabel = async (oldLabel: string, newLabel: string) => {
    if (!shopId) return;

    try {
      const updatedLabels = customLabels.map((label) =>
        label === oldLabel ? newLabel : label
      );

      await shop.update(shopId, { customLabels: updatedLabels });
      showSuccessToast(
        "Label Updated",
        "The label has been updated successfully."
      );
      syncWithSource();
    } catch (err) {
      console.error("An error occurred updating custom label: ", err);
      showErrorToast(
        "Label Failed to Update",
        `An error occurred updating custom label: ${err}`
      );
    }
  };

  const handleDeleteLabel = async (labelToDelete: string) => {
    if (!shopId) return;

    try {
      const updatedLabels = customLabels.filter(
        (label) => label !== labelToDelete
      );

      await shop.update(shopId, { customLabels: updatedLabels });
      showSuccessToast(
        "Label Deleted",
        "The label has been deleted successfully."
      );
      syncWithSource();
    } catch (err) {
      console.error("An error occurred deleting custom label: ", err);
      showErrorToast(
        "Label Failed to Delete",
        `An error occurred deleting custom label: ${err}`
      );
    }
  };

  const handleAddLabel = async (newLabel: string) => {
    if (!shopId) return;

    // Check if label already exists
    const labelExists = customLabels.some(
      (label) => label.toLowerCase() === newLabel.toLowerCase()
    );

    if (labelExists) {
      showErrorToast(
        "Label Already Exists",
        "A label with this name already exists."
      );
      return;
    }

    try {
      const updatedLabels = [...customLabels, newLabel];

      await shop.update(shopId, { customLabels: updatedLabels });
      showSuccessToast("Label Added", "The label has been added successfully.");
      syncWithSource();
    } catch (err) {
      console.error("An error occurred adding custom label: ", err);
      showErrorToast(
        "Label Failed to Add",
        `An error occurred adding custom label: ${err}`
      );
    }
  };

  const syncLabels = async () => {
    if (!shopData || !integration || !integration.integration) return;
    if (!integration) return;
    setSyncing(true);
    try {
      await httpsCallables.getLabels({
        shopId: shopData.id,
        integrator: integration.integration,
        apiPartnerId: integration.partnerId,
        apiTenantId: integration.tenantId,
        apiUrl: integration.apiUrl,
        apiToken: integration.apiKey,
      });
    } catch (e) {
      captureException(e);
    }
    setSyncing(false);
  };

  return (
    <div className="flex max-w-screen-sm flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Labels</h2>
          <p className="text-sm text-muted-foreground">
            Edit or delete labels. These are label names Shop Hero has seen for
            this shop, used when linking a label to a container in the layout
            builder. Only labels currently synced from{" "}
            {integration?.integration
              ? IntegratorsLabel[integration.integration]
              : "UNKNOWN"}
            &apos;s own label catalog are eligible for two-way (write-back) sync
            — check a container&apos;s label settings to see which of its linked
            labels sync back. Editing and removing labels does not update linked
            containers. You will need to manually update any linked containers.
            A label synced directly from{" "}
            {integration?.integration
              ? IntegratorsLabel[integration.integration]
              : "UNKNOWN"}
            &apos;s catalog can&apos;t be renamed or deleted here — manage it
            there instead.
          </p>
        </div>
      </div>
      <div className={"flex flex-row justify-center gap-2"}>
        {integration?.integration === Integrators.tekmetric ? (
          <AddCustomLabelDialog onAdd={handleAddLabel} />
        ) : (
          <Button variant={"ghost-alt"} onClick={syncLabels} disabled={syncing}>
            <Icon name={"download"} className="mr-2 h-4 w-4" />
            Download From{" "}
            {integration?.integration
              ? IntegratorsLabel[integration.integration]
              : "UNKNOWN"}
          </Button>
        )}
      </div>
      <CustomLabelList
        labels={customLabels}
        onEdit={handleSaveLabel}
        onDelete={handleDeleteLabel}
        integration={integration?.integration}
        labelCatalog={shopData.labelCatalog}
      />
    </div>
  );
};
